import { useState } from 'react';

import PropTypes from 'prop-types';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useLinkPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useIntl } from '@zenbusiness/application-commons-intl';

import { AiSlideIn } from '#client/components/AiSlideIn';
import { SLIDE_CONFIGS } from '#client/components/SlideInPanelHandler/slideConfigs';

import { UpsellFauxLink } from './OrderTracker.styles';
import OrderTrackerItem from './OrderTrackerItem';
import { getUpsellItems } from './utils';

function OrderTrackerUpsells({ type }) {
  const {
    entitlements, businessEntityUuid,
    businessEntity, businessEntity: { domesticJurisdiction }
  } = useBusinessEntity();
  const [openSlides, setOpenSlides] = useState({});
  const { formatMessage } = useIntl();

  const eventObject = {
    elementName: 'order_tracking_upsell'
  };
  const upsells = getUpsellItems(type, entitlements, domesticJurisdiction);

  const handleUpsellSlide = (entitlement) => {
    // Generic slide opening
    setOpenSlides((prev) => ({
      ...prev,
      [entitlement]: true
    }));
  };

  const handleCloseSlide = (entitlement) => {
    setOpenSlides((prev) => ({
      ...prev,
      [entitlement]: false
    }));
  };

  const upsellItems = upsells?.map(({
    entitlement, url, title, useSlideSheet
  }) => {
    if (useSlideSheet) {
      // For slide sheet items, handle click in OrderTrackerUpsells
      return (
        <OrderTrackerItem
          key={entitlement}
          color="secondary"
          type={entitlement}
          titleMessageId={title}
          ctaMessageId="dashboard.orderTracker.upsell.suggested"
          justifyContent="end"
          eventCallback={useLinkPressedCallback}
          eventObject={eventObject}
          onClick={() => handleUpsellSlide(entitlement)}
        />
      );
    }

    // For regular upsells, use UpsellFauxLink wrapper
    return (
      <UpsellFauxLink key={entitlement} url={url.replace('{BUUID}', businessEntityUuid)}>
        <OrderTrackerItem
          color="secondary"
          type={entitlement}
          titleMessageId={title}
          ctaMessageId="dashboard.orderTracker.upsell.suggested"
          justifyContent="end"
          eventCallback={useLinkPressedCallback}
          eventObject={eventObject}
        />
      </UpsellFauxLink>
    );
  });

  return (
    <>
      {upsellItems}
      {/* Render slide sheets for any upsells that have useSlideSheet: true */}
      {upsells?.filter((upsell) => upsell.useSlideSheet).map((upsell) => {
        const { entitlement } = upsell;

        // Map entitlement to slide component using generic AiSlideIn
        if (entitlement === 'ny_publication') {
          const config = SLIDE_CONFIGS['ny-pub-upsell'];
          const { aiConfig } = config;
          const businessEntityType = businessEntity?.businessEntityType || 'llc';
          const subtitleKey = aiConfig.subtitleKeys[businessEntityType]
            || aiConfig.subtitleKeys.llc;

          return (
            <AiSlideIn
              key={entitlement}
              aiSlideInOpen={!!openSlides[entitlement]}
              closeHandler={() => handleCloseSlide(entitlement)}
              title={formatMessage({ id: aiConfig.titleKey })}
              overline={formatMessage({ id: aiConfig.overlineKey })}
              subtitle={formatMessage({ id: subtitleKey })}
              promptSlug={aiConfig.promptSlug}
              queryType={aiConfig.queryType}
              fillData={{
                business_name: businessEntity?.name,
                location: businessEntity?.domesticJurisdiction,
                business_type: businessEntity?.businessEntityType
              }}
              eventingProperties={aiConfig.eventingProperties}
              errorMessage={formatMessage({ id: aiConfig.errorKey })}
              banner={{
                title: formatMessage({ id: aiConfig.banner.titleKey }),
                description: formatMessage({ id: aiConfig.banner.descriptionKey }),
                ctaText: formatMessage({ id: aiConfig.banner.ctaTextKey }),
                onCtaClick: () => aiConfig.banner.onCtaClick(
                  businessEntity,
                  () => handleCloseSlide(entitlement),
                  entitlement
                )
              }}
            />
          );
        }

        // Add other slide sheet components here as needed
        return null;
      })}
    </>
  );
}

OrderTrackerUpsells.propTypes = {
  type: PropTypes.string.isRequired
};

export default OrderTrackerUpsells;
